import axiosClient from "../api/axiosClient";

/**
 * Create Event Service
 * All endpoints used by the "Create Event" flow, ported 1:1 from the
 * React Native app's src/services/api/index.js + src/services/constants/index.js.
 */

// --- Event limit gate ---
export const checkEventLimit = (eventType = "Public") =>
  axiosClient.get("/Events/checkEventLimit", { params: { eventType } });

export const requestEventLimitOverride = (eventType) =>
  axiosClient.post("/Events/RequestEventLimitOverride", { eventType });

// --- Categories ---
export const getEventCategories = (eventType = "Public") =>
  axiosClient.get("/EventCategories", { params: { eventType } });

// --- Create / update event ---
/** @param {object} payload - plain object; sent as a JSON string body, matching mobile's onCreateEvnt. */
export const createEvent = (payload) =>
  axiosClient.post("/Events", JSON.stringify(payload));

/** Edit an existing event — same payload shape as createEvent, matching mobile's onCreateEvnt(isEdit=true). */
export const updateEvent = (id, payload) =>
  axiosClient.put(`/Events/${id}`, JSON.stringify(payload));

// --- Event title images ---
/**
 * Upload up to 3 event title images.
 * @param {File[]} files
 * @param {number} [countryCode=49]
 */
export const uploadEventImages = (files, countryCode = 49) => {
  const formData = new FormData();
  formData.append("CountryCode", String(countryCode));
  formData.append("folderName", "Event_Title");
  files.forEach((file) => formData.append("files", file));
  return axiosClient.post("/Image/BulkCompressed", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
};

/**
 * Upload a GIF + a client-generated static thumbnail together.
 * Mirrors mobile's ConvertVideoToGif upload (src/Screens/convert-video-to-gif/index.js)
 * exactly — a *different* endpoint from uploadEventImages: `Image/Bulk`, not
 * `Image/BulkCompressed`. That distinction matters: BulkCompressed re-encodes
 * to a static webp (fine for photos, but it silently destroys GIF animation),
 * while Bulk stores the file as-is.
 * @param {File} gifFile
 * @param {File} thumbnailFile
 * @param {number} [countryCode=49]
 */
export const uploadGifWithThumbnail = (gifFile, thumbnailFile, countryCode = 49) => {
  const formData = new FormData();
  formData.append("CountryCode", String(countryCode));
  formData.append("isCompressed", true);
  formData.append("folderName", "Event_Title");
  formData.append("files", gifFile);
  formData.append("files", thumbnailFile);
  return axiosClient.post("/Image/Bulk", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
};

// --- OTP (production create-event verification) ---
export const sendPrivateEventOTP = () => axiosClient.get("/Events/Private/OTP");

export const validateEventOTP = (otp) =>
  axiosClient.post(`/Events/Private/${otp}`);

// --- Saved locations (ELQR) ---
export const getMyELQRs = (pageNumber = 1, pageSize = 30) =>
  axiosClient.get("/Account/GetMyELQR", { params: { pageNumber, pageSize } });

export const getELQRById = (id) =>
  axiosClient.get(`/Account/GetMyELQRById/${id}`);

/**
 * @param {{locationName, address, latitude, longitude, city, country, radius,
 *   isGeofenceEnabled, geofencePolygonJson, beforeMinutes, afterMinutes}} body
 */
export const generateELQR = (body) =>
  axiosClient.post("/Account/GenerateELQR", JSON.stringify(body));

export const updateELQR = (id, body) =>
  axiosClient.post(`/Account/UpdateELQR/${id}`, JSON.stringify(body));

/** Soft-delete a saved location (mobile does this via UpdateELQR too). */
export const deleteELQR = (id, radius) =>
  updateELQR(id, { isActive: false, radius });

// --- Location check-in points ---
export const createLocationCheckInPoint = (body) =>
  axiosClient.post("/LocationCheckInPoint/Create", JSON.stringify(body));

export const updateLocationCheckInPoint = (body) =>
  axiosClient.put("/LocationCheckInPoint/Update", JSON.stringify(body));

export const deleteLocationCheckInPoint = (checkInPointId) =>
  axiosClient.delete(`/LocationCheckInPoint/Delete/${checkInPointId}`);
